import { differenceInMinutes, sub } from 'date-fns';
import { toDate, utcToZonedTime } from 'date-fns-tz';
/**
 * InsuranceCompanyCodes is used to reference any codes needed
 * for application logic. These codes will most-likely be related
 * to Zurich entities.
 */
export var InsuranceCompanyCodes;
(function (InsuranceCompanyCodes) {
    InsuranceCompanyCodes["ZURICH"] = "25";
    InsuranceCompanyCodes["FOREIGN"] = "998";
})(InsuranceCompanyCodes || (InsuranceCompanyCodes = {}));
export var TriStateType;
(function (TriStateType) {
    TriStateType["TRUE"] = "true";
    TriStateType["FALSE"] = "false";
    TriStateType["UNSET"] = "unset";
})(TriStateType || (TriStateType = {}));
(function (TriStateType) {
    function getDefault() {
        return TriStateType.UNSET;
    }
    TriStateType.getDefault = getDefault;
    function isEmpty(value) {
        return value === TriStateType.UNSET;
    }
    TriStateType.isEmpty = isEmpty;
    function getValues() {
        return [TriStateType.TRUE, TriStateType.FALSE, TriStateType.UNSET];
    }
    TriStateType.getValues = getValues;
    function getValue(value) {
        switch (value) {
            case 'true':
                return true;
            case 'false':
                return false;
        }
        return null;
    }
    TriStateType.getValue = getValue;
    function fromString(value) {
        const isEnum = convertValueToEnum(value, TriStateType.getValues(), TriStateType.UNSET);
        if (!isEnum) {
            throw new Error('Value is not of type TriStateType');
        }
        return isEnum;
    }
    TriStateType.fromString = fromString;
    // if the passed value is undefined, the alt value is returned
    // if the passed value is null, null is returned
    // if the passed value is a string, TriStateType.getValue() returns the appropriate boolean value
    function getAltValue(value, alt) {
        return value === undefined ? alt : getValue(value);
    }
    TriStateType.getAltValue = getAltValue;
    function fromBoolean(value) {
        switch (value) {
            case true:
                return TriStateType.TRUE;
            case false:
                return TriStateType.FALSE;
            default:
                return TriStateType.UNSET;
        }
    }
    TriStateType.fromBoolean = fromBoolean;
    function toLocale(value, t) {
        const isEnum = convertValueToEnum(value, TriStateType.getValues(), TriStateType.UNSET);
        if (!isEnum) {
            throw new Error('Value is not of type TriStateType');
        }
        switch (value) {
            case TriStateType.TRUE:
                return t('common:yes');
            case TriStateType.FALSE:
                return t('common:no');
            default:
                return t('common:unset');
        }
    }
    TriStateType.toLocale = toLocale;
    function createOptions(t) {
        return [
            {
                label: t('common:yes'),
                value: TriStateType.TRUE,
            },
            {
                label: t('common:no'),
                value: TriStateType.FALSE,
            },
        ];
    }
    TriStateType.createOptions = createOptions;
})(TriStateType || (TriStateType = {}));
/* Usage:

  const example: string | undefined = 'asdf';
  const test = convertValueToEnum(
    example,
    Object.values(LanguageKey),
    LanguageKey.De
  );

  Output: test will be LanguageKey.De, because 'asdf' is not part of the LanguageKey enum. So the defaultValue is returned.
*/
export function convertValueToEnum(value, enumValues, defaultValue) {
    if (value === undefined ||
        !enumValues.includes(value) // we have to try with the enum value's type
    ) {
        return defaultValue;
    }
    return value; // no we know for sure, that we can apply this type
}
export var LanguageKey;
(function (LanguageKey) {
    LanguageKey["De"] = "de";
})(LanguageKey || (LanguageKey = {}));
export var SalesChannel;
(function (SalesChannel) {
    SalesChannel["DIRECT"] = "direct";
    SalesChannel["BROKER"] = "broker";
    SalesChannel["HBM"] = "hbm";
    SalesChannel["NON"] = "non";
})(SalesChannel || (SalesChannel = {}));
export var ReferenceKey;
(function (ReferenceKey) {
    ReferenceKey["CLAIM"] = "claim";
    ReferenceKey["POLICY"] = "policy";
    ReferenceKey["PRODUCT"] = "product";
    ReferenceKey["ADVISORY"] = "advisory";
    ReferenceKey["CUSTOMER"] = "customer";
    ReferenceKey["FLOWDEFINITION"] = "flowDefinition";
})(ReferenceKey || (ReferenceKey = {}));
export function convertDateToLocalISODateTime(date) {
    // Move date to local timezone
    const localDate = new Date(date);
    localDate.setMinutes(date.getMinutes() - date.getTimezoneOffset());
    return localDate.toISOString();
}
export function convertDateToLocalISODate(date) {
    // extract the iso date part
    const dateString = convertDateToLocalISODateTime(date).split('T').shift();
    if (!dateString)
        throw new Error('Invalid date conversion!');
    return dateString;
}
export function convertApiDateToDateString(apiDate, invalidDefault = '') {
    const unixTimestamp = Date.parse(apiDate);
    if (isNaN(unixTimestamp)) {
        return invalidDefault;
    }
    return new Date(unixTimestamp).toLocaleDateString();
}
export function convertApiDateTimeToDateString(apiDateTime) {
    return new Date(apiDateTime.toString()).toLocaleDateString();
}
export function apiDateToDate(timeZoneClient, timeZoneServer, apiDate) {
    return new Date(apiDate);
}
export function apiDateTimeToDate(timeZoneClient, timeZoneServer, apiDateTime) {
    return new Date(apiDateTime.toString());
}
export function dateToApiDate(timeZoneClient, timeZoneServer, date) {
    // we throw away the time part as the backend only wants the date
    return dateToApiDateTime(timeZoneClient, timeZoneServer, date)
        .split('T')
        .shift();
}
export function dateToApiDateTime(timeZoneClient, timeZoneServer, date) {
    const dateUTC = utcToZonedTime(date, timeZoneClient);
    const dateUTCWithoutTimeZone = dateUTC.toISOString().slice(0, -1);
    const apiDateServerPrime = toDate(dateUTCWithoutTimeZone, {
        timeZone: timeZoneServer,
    });
    const minutes = differenceInMinutes(apiDateServerPrime, dateUTC);
    const apiDateServer = sub(dateUTC, { minutes });
    // TODO: remove if refactoring branch is merged
    // There are edge cases where the datepicker does not behave as expected. It is partly because the utcToZonedTime(date-fn-tz) method converts an invalid date to a valid one.
    // Example:
    // '21.02.2' -> dateToApiDate method
    // before: Thu Feb 21 0002 00:00:00 GMT+0105
    // after: Fri Feb 21 1902 00:00:00 GMT+0100
    if (date.getFullYear() !== dateUTC.getFullYear()) {
        apiDateServer.setFullYear(date.getFullYear());
    }
    // Keep the whole date as yyyy-MM-dd'T'HH:mm:ss.SSS'Z'
    return apiDateServer.toISOString();
}
